import { useState, useEffect } from "react";
import { 
  Plus, Search, Edit2, Trash2, Eye, EyeOff, MapPin, X, Upload,
  Mountain, Trees, Landmark, Waves, Church, Utensils, Binoculars,
  TreePine, Image as ImageIcon, Facebook
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import AdminLayout from "@/components/layout/AdminLayout";
import { LocationInput } from "@/components/shared/LocationPickerV2";
import { API } from "@/App";
import axios from "axios";
import AdminPagination from "@/components/admin/AdminPagination";

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  return url.startsWith('/uploads/') ? `${backendUrl}/api${url}` : `${backendUrl}${url}`;
};

const categoryIcons = {
  natural: Trees,
  historical: Landmark,
  beach: Waves,
  adventure: Mountain,
  cultural: Landmark,
  religious: Church,
  gastronomy: Utensils,
  viewpoint: Binoculars,
  park: TreePine,
  other: MapPin,
};

const initialFormData = {
  name: "",
  slug: "",
  category: "natural",
  description: "",
  short_description: "",
  municipio: "",
  departamento: "Choluteca",
  address: "",
  lat: null,
  lng: null,
  how_to_get_there: "",
  distances: "",
  best_time_to_visit: "",
  entry_fee: "",
  additional_info: "",
  phone: "",
  whatsapp: "",
  website: "",
  facebook: "",
  instagram: "",
  featured_image_id: "",
  featured_image_url: "",
  gallery_image_ids: [],
  gallery_urls: [],
  facebook_videos: [""],
  tags: "",
  status: "draft",
};

export default function AdminDestinos() {
  const [destinations, setDestinations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [total, setTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [mediaList, setMediaList] = useState([]);
  const [mediaLoading, setMediaLoading] = useState(false);
  
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [mediaDialogOpen, setMediaDialogOpen] = useState(false);
  const [mediaSelectMode, setMediaSelectMode] = useState("featured"); // "featured" | "gallery"
  
  const [formData, setFormData] = useState(initialFormData);
  const [editingId, setEditingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const token = localStorage.getItem("session_token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchDestinations();
    fetchCategories();
  }, [searchQuery, statusFilter, categoryFilter, page, limit]);

  const fetchDestinations = async () => {
    try {
      const params = new URLSearchParams();
      params.append("skip", String((page - 1) * limit));
      params.append("limit", String(limit));
      if (searchQuery) params.append("search", searchQuery);
      if (statusFilter) params.append("status", statusFilter);
      if (categoryFilter) params.append("category", categoryFilter);
      
      const response = await axios.get(`${API}/destinations?${params}`, { headers });
      setDestinations(response.data.items || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error("Error fetching destinations:", error);
      toast.error("Error al cargar destinos");
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/destination/categories`);
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const handleOpenDialog = (destination = null) => {
    if (destination) {
      setFormData({
        ...initialFormData,
        ...destination,
        tags: destination.tags?.join(", ") || "",
        facebook_videos: destination.facebook_videos?.length > 0 ? destination.facebook_videos : [""],
      });
      setEditingId(destination.destination_id);
    } else {
      setFormData(initialFormData);
      setEditingId(null);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setFormData(initialFormData);
    setEditingId(null);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleLocationChange = ({ lat, lng }) => {
    setFormData(prev => ({ ...prev, lat, lng }));
  };

  const handleOpenMediaDialog = async (mode) => {
    setMediaSelectMode(mode);
    setMediaDialogOpen(true);
    setMediaLoading(true);
    try {
      const response = await axios.get(`${API}/media?limit=50`, { headers });
      setMediaList(response.data.items || []);
    } catch (error) {
      console.error("Error fetching media:", error);
    } finally {
      setMediaLoading(false);
    }
  };

  const handleMediaSelect = (media) => {
    if (mediaSelectMode === "featured") {
      setFormData(prev => ({
        ...prev,
        featured_image_id: media.media_id,
        featured_image_url: media.url,
      }));
      setMediaDialogOpen(false);
    } else if (mediaSelectMode === "gallery") {
      setFormData(prev => ({
        ...prev,
        gallery_image_ids: [...prev.gallery_image_ids, media.media_id],
        gallery_urls: [...prev.gallery_urls, { id: media.media_id, url: media.url }],
      }));
    }
  };

  const handleRemoveGalleryImage = (imageId) => {
    setFormData(prev => ({
      ...prev,
      gallery_image_ids: prev.gallery_image_ids.filter(id => id !== imageId),
      gallery_urls: prev.gallery_urls.filter(img => img.id !== imageId),
    }));
  };

  const handleAddVideoField = () => {
    setFormData(prev => ({
      ...prev,
      facebook_videos: [...prev.facebook_videos, ""],
    }));
  };

  const handleRemoveVideoField = (index) => {
    setFormData(prev => ({
      ...prev,
      facebook_videos: prev.facebook_videos.filter((_, i) => i !== index),
    }));
  };

  const handleVideoChange = (index, value) => {
    setFormData(prev => ({
      ...prev,
      facebook_videos: prev.facebook_videos.map((v, i) => i === index ? value : v),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.category) {
      toast.error("Nombre y categoría son requeridos");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        tags: formData.tags ? formData.tags.split(",").map(t => t.trim()) : [],
        facebook_videos: formData.facebook_videos.filter(v => v.trim() !== ""),
        lat: formData.lat || null,
        lng: formData.lng || null,
      };

      if (editingId) {
        await axios.put(`${API}/destinations/${editingId}`, payload, { headers });
        toast.success("Destino actualizado");
      } else {
        await axios.post(`${API}/destinations`, payload, { headers });
        toast.success("Destino creado");
      }
      
      handleCloseDialog();
      fetchDestinations();
    } catch (error) {
      console.error("Error saving destination:", error);
      toast.error("Error al guardar destino");
    } finally {
      setSaving(false);
    }
  };

  const handleQuickStatus = async (destination, newStatus) => {
    try {
      const payload = {
        ...destination,
        tags: destination.tags || [],
        facebook_videos: destination.facebook_videos || [],
        status: newStatus,
      };
      await axios.put(`${API}/destinations/${destination.destination_id}`, payload, { headers });
      toast.success(`Destino ${newStatus === "published" ? "publicado" : "despublicado"}`);
      fetchDestinations();
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Error al cambiar estado");
    }
  };

  const handleDelete = async () => {
    if (!deletingId) return;
    try {
      await axios.delete(`${API}/destinations/${deletingId}`, { headers });
      toast.success("Destino eliminado");
      setDeleteDialogOpen(false);
      setDeletingId(null);
      fetchDestinations();
    } catch (error) {
      console.error("Error deleting destination:", error);
      toast.error("Error al eliminar destino");
    }
  };

  const getCategoryIcon = (categoryId) => {
    const IconComponent = categoryIcons[categoryId] || MapPin;
    return <IconComponent className="h-4 w-4" />;
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Destinos Turísticos</h1>
            <p className="text-gray-500">Gestiona los destinos turísticos de la región</p>
          </div>
          <Button onClick={() => handleOpenDialog()} data-testid="new-destination-btn">
            <Plus className="h-4 w-4 mr-2" />
            Nuevo Destino
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Buscar destinos..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="published">Publicado</SelectItem>
              <SelectItem value="draft">Borrador</SelectItem>
            </SelectContent>
          </Select>
          <Select value={categoryFilter || "all"} onValueChange={(v) => setCategoryFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Categoría" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              {categories.map(cat => (
                <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Destino</TableHead>
                <TableHead>Categoría</TableHead>
                <TableHead>Ubicación</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Vistas</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto" />
                  </TableCell>
                </TableRow>
              ) : destinations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                    No hay destinos registrados
                  </TableCell>
                </TableRow>
              ) : (
                destinations.map((dest) => (
                  <TableRow key={dest.destination_id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {dest.featured_image_url ? (
                          <img
                            src={getImageUrl(dest.featured_image_url)}
                            alt=""
                            className="w-12 h-12 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-lg bg-amber-100 flex items-center justify-center">
                            <Mountain className="h-6 w-6 text-amber-600" />
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-gray-900">{dest.name}</p>
                          <p className="text-sm text-gray-500 line-clamp-1">
                            {dest.short_description || dest.description?.slice(0, 50)}
                          </p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getCategoryIcon(dest.category)}
                        <span className="text-sm">{dest.category_name}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <p>{dest.municipio || "—"}</p>
                        {dest.lat && dest.lng && (
                          <span className="text-xs text-green-600 flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            En mapa
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={dest.status === "published" ? "default" : "secondary"}>
                        {dest.status === "published" ? "Publicado" : "Borrador"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-gray-600">{dest.view_count || 0}</span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleQuickStatus(dest, dest.status === "published" ? "draft" : "published")}
                          title={dest.status === "published" ? "Despublicar" : "Publicar"}
                        >
                          {dest.status === "published" ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleOpenDialog(dest)}>
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-red-500 hover:text-red-600"
                          onClick={() => { setDeletingId(dest.destination_id); setDeleteDialogOpen(true); }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingId ? "Editar Destino" : "Nuevo Destino"}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left Column */}
              <div className="space-y-4">
                <div>
                  <Label>Nombre del destino *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                    placeholder="Ej: Cascadas de San Antonio"
                    required
                  />
                </div>

                <div>
                  <Label>Categoría *</Label>
                  <Select value={formData.category} onValueChange={(v) => handleChange("category", v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(cat => (
                        <SelectItem key={cat.id} value={cat.id}>
                          <div className="flex items-center gap-2">
                            {getCategoryIcon(cat.id)}
                            {cat.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Descripción corta</Label>
                  <Input
                    value={formData.short_description}
                    onChange={(e) => handleChange("short_description", e.target.value)}
                    placeholder="Una línea resumiendo el destino"
                  />
                </div>

                <div>
                  <Label>Descripción completa</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => handleChange("description", e.target.value)}
                    placeholder="Descripción detallada del destino..."
                    rows={4}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Municipio</Label>
                    <Input
                      value={formData.municipio}
                      onChange={(e) => handleChange("municipio", e.target.value)}
                      placeholder="Ej: Choluteca"
                    />
                  </div>
                  <div>
                    <Label>Departamento</Label>
                    <Input
                      value={formData.departamento}
                      onChange={(e) => handleChange("departamento", e.target.value)}
                      placeholder="Ej: Choluteca"
                    />
                  </div>
                </div>

                <div>
                  <Label>Dirección / Cómo llegar</Label>
                  <Textarea
                    value={formData.how_to_get_there}
                    onChange={(e) => handleChange("how_to_get_there", e.target.value)}
                    placeholder="Indicaciones de cómo llegar al destino..."
                    rows={2}
                  />
                </div>

                <div>
                  <Label>Distancias desde diferentes lugares</Label>
                  <Textarea
                    value={formData.distances}
                    onChange={(e) => handleChange("distances", e.target.value)}
                    placeholder="Ej: Desde Choluteca: 45 min&#10;Desde Tegucigalpa: 2 horas&#10;Desde San Lorenzo: 30 min"
                    rows={3}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Mejor época para visitar</Label>
                    <Input
                      value={formData.best_time_to_visit}
                      onChange={(e) => handleChange("best_time_to_visit", e.target.value)}
                      placeholder="Ej: Noviembre a Abril"
                    />
                  </div>
                  <div>
                    <Label>Costo de entrada</Label>
                    <Input
                      value={formData.entry_fee}
                      onChange={(e) => handleChange("entry_fee", e.target.value)}
                      placeholder="Ej: L. 50 o Gratis"
                    />
                  </div>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-4">
                {/* Additional Info */}
                <div>
                  <Label>Información adicional</Label>
                  <Textarea
                    value={formData.additional_info}
                    onChange={(e) => handleChange("additional_info", e.target.value)}
                    placeholder="Cualquier información adicional relevante para los visitantes..."
                    rows={3}
                  />
                </div>

                {/* Featured Image */}
                <div>
                  <Label>Imagen principal</Label>
                  {formData.featured_image_url ? (
                    <div className="relative mt-2 w-full">
                      <img
                        src={getImageUrl(formData.featured_image_url)}
                        alt=""
                        className="w-full aspect-video object-cover rounded-lg"
                      />
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        className="absolute bottom-2 right-2"
                        onClick={() => handleOpenMediaDialog("featured")}
                      >
                        Cambiar
                      </Button>
                    </div>
                  ) : (
                    <Button
                      type="button"
                      variant="outline"
                      className="mt-2 w-full"
                      onClick={() => handleOpenMediaDialog("featured")}
                    >
                      <ImageIcon className="h-4 w-4 mr-2" />
                      Seleccionar imagen
                    </Button>
                  )}
                </div>

                {/* Gallery */}
                <div>
                  <Label>Galería de fotos</Label>
                  <div className="mt-2 space-y-3">
                    {formData.gallery_urls && formData.gallery_urls.length > 0 && (
                      <div className="grid grid-cols-4 gap-2">
                        {formData.gallery_urls.map((img, index) => (
                          <div key={img.id || index} className="relative aspect-square rounded-lg overflow-hidden group">
                            <img
                              src={getImageUrl(img.url || img)}
                              alt=""
                              className="w-full h-full object-cover"
                            />
                            <button
                              type="button"
                              onClick={() => handleRemoveGalleryImage(img.id)}
                              className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => handleOpenMediaDialog("gallery")}
                      className="w-full"
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      {formData.gallery_urls?.length > 0 ? "Agregar más fotos" : "Agregar fotos"}
                    </Button>
                  </div>
                </div>

                {/* Location */}
                <LocationInput
                  lat={formData.lat}
                  lng={formData.lng}
                  onChange={handleLocationChange}
                  label="Ubicación en el mapa"
                />

                {/* Facebook Videos */}
                <div>
                  <Label className="flex items-center gap-2">
                    <Facebook className="h-4 w-4 text-blue-600" />
                    Videos de Facebook
                  </Label>
                  <div className="mt-2 space-y-2">
                    {formData.facebook_videos.map((video, index) => (
                      <div key={index} className="flex gap-2">
                        <Input
                          value={video}
                          onChange={(e) => handleVideoChange(index, e.target.value)}
                          placeholder="https://www.facebook.com/watch/?v=..."
                        />
                        {formData.facebook_videos.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => handleRemoveVideoField(index)}
                            className="text-red-500"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleAddVideoField}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Agregar video
                    </Button>
                  </div>
                </div>

                {/* Contact & Social */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Teléfono</Label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => handleChange("phone", e.target.value)}
                      placeholder="+504 ..."
                    />
                  </div>
                  <div>
                    <Label>WhatsApp</Label>
                    <Input
                      value={formData.whatsapp}
                      onChange={(e) => handleChange("whatsapp", e.target.value)}
                      placeholder="+504 ..."
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Facebook</Label>
                    <Input
                      value={formData.facebook}
                      onChange={(e) => handleChange("facebook", e.target.value)}
                      placeholder="URL de Facebook"
                    />
                  </div>
                  <div>
                    <Label>Instagram</Label>
                    <Input
                      value={formData.instagram}
                      onChange={(e) => handleChange("instagram", e.target.value)}
                      placeholder="URL de Instagram"
                    />
                  </div>
                </div>

                <div>
                  <Label>Tags (separados por coma)</Label>
                  <Input
                    value={formData.tags}
                    onChange={(e) => handleChange("tags", e.target.value)}
                    placeholder="Ej: naturaleza, cascadas, familia"
                  />
                </div>

                <div>
                  <Label>Estado</Label>
                  <Select value={formData.status} onValueChange={(v) => handleChange("status", v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Borrador</SelectItem>
                      <SelectItem value="published">Publicado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Cancelar
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? "Guardando..." : editingId ? "Actualizar" : "Crear"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Media Library Dialog */}
      <Dialog open={mediaDialogOpen} onOpenChange={setMediaDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>
              Seleccionar {mediaSelectMode === "featured" ? "imagen principal" : "imágenes para galería"}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto">
            {mediaLoading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
              </div>
            ) : (
              <div className="grid grid-cols-4 sm:grid-cols-6 gap-3 p-1">
                {mediaList.map((media) => (
                  <button
                    key={media.media_id}
                    type="button"
                    onClick={() => handleMediaSelect(media)}
                    className="aspect-square rounded-lg overflow-hidden border-2 border-transparent hover:border-primary transition-colors"
                  >
                    <img
                      src={getImageUrl(media.url)}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
                {mediaList.length === 0 && (
                  <div className="col-span-full text-center py-12 text-gray-500">
                    No hay imágenes disponibles
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMediaDialogOpen(false)}>
              {mediaSelectMode === "gallery" ? "Listo" : "Cancelar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar destino?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. El destino será eliminado permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
        <AdminPagination
          page={page}
          total={total}
          limit={limit}
          onPageChange={setPage}
          onLimitChange={(l) => { setLimit(l); setPage(1); }}
          testId="destinos-pagination"
          label="destinos"
        />
    </AdminLayout>
  );
}
